import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertIcon } from '@/components/catalog/icons';
import {
  Badge,
  Button,
  Combobox,
  EmptyState,
  Field,
  Input,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import {
  daysAgoLocal,
  formatLatency,
  nextDayLocal,
  statusTone,
  todayLocal,
} from '@/lib/api-log-utils';
import { actionLabel, moduleLabel } from '@/lib/activity-language';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import type {
  ApiLogGroupBy,
  ApiLogStatRow,
  ApiLogStatsParams,
  ApiLogStatsResponse,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * `GET /api-logs/stats` — the aggregate side of the activity log.
 *
 * Read as a business question: where is the activity, and what share of it is
 * failing? The grouping options, the bucket values and the counts are all
 * phrased for someone who does not know what a route pattern is; the response
 * times stay, because they are why support opens this page, but they sit
 * beneath the figures a manager reads.
 *
 * The bucket VALUES go through `moduleLabel` / `actionLabel` from
 * `lib/activity-language.ts` so a row reads "Deals", not "deals" — the same
 * vocabulary the feed and the detail dialog use.
 *
 * Three things about this endpoint shape the whole screen:
 *
 *  1. **The window is never "everything".** `from` defaults to seven days ago
 *     server-side. A reader who assumes the numbers cover all time draws the
 *     wrong conclusion from every one of them, so the effective window is
 *     printed from `meta` — the server's echo — rather than from local state,
 *     which is blank on a first visit precisely when the default applies.
 *
 *  2. **`bucket` may be `null`, and null is a REAL bucket**, not missing data.
 *     Grouped by screen it is the requests that matched nothing (the 404s);
 *     grouped by area it is sign-in, health and the permission endpoints, which
 *     sit outside the permission model. Dropping the row would hide the 404
 *     flood, which is one of the more useful things on this page.
 *
 *  3. **`bucket`'s type follows `groupBy`** — a number for `statusCode`, a
 *     BIGINT-as-string for `userId` (a float would lose digits), a plain string
 *     for the rest. The renderer branches on it rather than `String()`-ing the
 *     distinction away: the status code wants a status-toned badge, and the
 *     user id must survive as text.
 *
 * The rows are capped at `limit` and ordered by volume, so every total here is
 * "across the rows shown", never "across the window". The footer says so.
 */

const DEFAULT_GROUP_BY: ApiLogGroupBy = 'route';
const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;
const LIMIT_OPTIONS = [10, 20, 50, 100] as const;
const SKELETON_ROWS = 6;

interface Grouping {
  value: ApiLogGroupBy;
  /** Control label, in business words. */
  label: string;
  /** What one row is, for the copy around the list. */
  noun: string;
  nounPlural: string;
  /** The `GET /api-logs` query key this bucket can be filtered on. */
  listParam: string;
  /** What a `null` bucket means for THIS grouping. Never rendered blank. */
  nullLabel: string;
}

/**
 * The six groupings, named for what a reader is asking rather than for the
 * column they aggregate. `value` and `listParam` are still the contract's own
 * words — those go on the wire and must not be prettified.
 */
const GROUPINGS: readonly Grouping[] = [
  {
    value: 'route',
    label: 'Screen or endpoint',
    noun: 'screen',
    nounPlural: 'screens',
    listParam: 'route',
    nullLabel: 'Nothing matched',
  },
  {
    value: 'moduleSlug',
    label: 'Area',
    noun: 'area',
    nounPlural: 'areas',
    listParam: 'moduleSlug',
    nullLabel: 'Outside the business areas',
  },
  {
    value: 'actionSlug',
    label: 'What happened',
    noun: 'activity type',
    nounPlural: 'activity types',
    listParam: 'actionSlug',
    nullLabel: 'Nothing recognisable',
  },
  {
    value: 'statusCode',
    label: 'Result',
    noun: 'result',
    nounPlural: 'results',
    listParam: 'statusCode',
    nullLabel: 'No result recorded',
  },
  {
    value: 'userId',
    label: 'Person',
    noun: 'person',
    nounPlural: 'people',
    listParam: 'userId',
    nullLabel: 'Nobody signed in',
  },
  {
    value: 'method',
    label: 'Request type',
    noun: 'request type',
    nounPlural: 'request types',
    listParam: 'method',
    nullLabel: 'No request type recorded',
  },
];

const FALLBACK_GROUPING: Grouping = GROUPINGS[0] as Grouping;

/**
 * The six groupings as `Combobox` rows — searchable, per the owner's 2026-09-13
 * ruling that every filter dropdown in this app is a `Combobox` whatever its
 * option count. Mapped rather than handed over as-is: `GROUPINGS` is `readonly`
 * and carries four more keys than a row needs.
 *
 * ⛔ **NO `emptyOptionLabel` AND NO `placeholder`, AND THAT IS LOAD-BEARING.**
 * This is not a filter — it decides `groupBy`, which IS the question the
 * endpoint is asked, so there is no "any" state and never was one. A blank row
 * would write `groupBy: ''`; `setParams` drops an empty value from the URL
 * entirely (an empty query param is a 422 here, not a default), so the page
 * would silently fall back to `route` while the control read as empty. The
 * placeholder is unreachable for the same reason the blank row must not exist:
 * `parseGroupBy` always returns a real member, so a selection is always in force.
 */
const GROUPING_OPTIONS: ComboboxOption[] = GROUPINGS.map(({ value, label }) => ({ value, label }));

/** The offered row caps. `parseLimit` still refuses anything outside 1..100. */
const LIMIT_COMBOBOX_OPTIONS: ComboboxOption[] = LIMIT_OPTIONS.map((option) => ({
  value: String(option),
  label: String(option),
}));

function groupingFor(value: ApiLogGroupBy): Grouping {
  return GROUPINGS.find((entry) => entry.value === value) ?? FALLBACK_GROUPING;
}

function parseGroupBy(raw: string | null): ApiLogGroupBy {
  const match = GROUPINGS.find((entry) => entry.value === raw);
  return match ? match.value : DEFAULT_GROUP_BY;
}

/** 1..100 — over the ceiling is a 422, so an out-of-range URL falls back. */
function parseLimit(raw: string | null): number {
  if (!raw) return DEFAULT_LIMIT;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1 || value > MAX_LIMIT) return DEFAULT_LIMIT;
  return value;
}

/** Only a plain 'YYYY-MM-DD' is accepted; anything else is treated as absent. */
function parseDate(raw: string | null): string | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  return raw;
}

/**
 * A window bound as the server echoed it.
 *
 * `meta.from` can come back as a bare date or as a full timestamp. A bare date
 * put through `new Date()` is UTC midnight and renders as the previous day west
 * of Greenwich, so the two cases go to different helpers rather than to one
 * that is wrong half the time.
 */
function formatStamp(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatDateOnly(iso) : formatDateTime(iso);
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/** `null` is "not recorded" — not zero, and never NaN. */
function formatNullableLatency(ms: number | null): string {
  return ms === null ? '—' : formatLatency(ms);
}

/**
 * Failures as a share of requests — the figure an admin is actually scanning
 * for. `errors` on the wire; "failures" on the page.
 */
function errorRate(row: ApiLogStatRow): number | null {
  return row.hits > 0 ? row.errors / row.hits : null;
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

/**
 * The row's identity as a React key.
 *
 * The type is kept in the key because two groupings can produce the same text —
 * and because `userId` is a BIGINT the pool hands back as a string so no digits
 * are lost to a float. It is passed through untouched, never parsed.
 */
function bucketKey(bucket: string | number | null): string {
  if (bucket === null) return 'bucket:null';
  return typeof bucket === 'number' ? `n:${bucket}` : `s:${bucket}`;
}

/**
 * A string bucket in the reader's words.
 *
 * Only the two slug groupings have a business vocabulary; a route pattern and
 * an HTTP method are already themselves. `moduleLabel`/`actionLabel` return
 * null for a slug they do not know, and the raw value is a better answer than
 * a blank, so it falls through.
 */
function bucketText(grouping: Grouping, bucket: string): string {
  if (grouping.value === 'moduleSlug') return moduleLabel(bucket) ?? bucket;
  if (grouping.value === 'actionSlug') return actionLabel(bucket) ?? bucket;
  return bucket;
}

export function ApiLogStatsPage() {
  const { ability } = useAuth();
  // The activity log is admin-only even to read; `can` already encodes that rule.
  const canRead = ability.can('read', 'api-logs');

  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<ApiLogStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /*
   * The serialised query is the effect dependency, not the parsed object: a
   * fresh object every render would refetch on every render. A string compares
   * by value.
   */
  const queryString = searchParams.toString();

  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const fromParam = parseDate(searchParams.get('from'));
  const toParam = parseDate(searchParams.get('to'));
  const limit = parseLimit(searchParams.get('limit'));
  const grouping = groupingFor(groupBy);

  const load = useCallback(
    (signal: AbortSignal) => {
      setLoading(true);
      const current = new URLSearchParams(queryString);
      const params: ApiLogStatsParams = {
        groupBy: parseGroupBy(current.get('groupBy')),
        limit: parseLimit(current.get('limit')),
      };
      // Blank keys are omitted, never sent empty: `?from=` is a 422 rather than
      // "no filter". An absent `from` is how the server's 7-day default applies.
      const from = parseDate(current.get('from'));
      if (from) params.from = from;
      const to = parseDate(current.get('to'));
      /*
       * `to` is INCLUSIVE and a bare date parses as midnight, so `to=2026-08-24`
       * means "up to the very start of the 24th" and silently drops that whole
       * day's traffic. "Through the 24th" has to be sent as the 25th, which is
       * exactly what `nextDayLocal` is for.
       */
      if (to) params.to = nextDayLocal(to);

      return api
        .apiLogStats(params, signal)
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            /*
             * A 403 carries no `details`, so there is nothing to render per
             * field — it is a banner. It means the grant went away between the
             * render that showed this page and the request it made.
             */
            setError(
              'Your account no longer has permission to read the activity log. Ask an administrator.',
            );
            return;
          }
          setError(caught instanceof Error ? caught.message : 'Failed to load activity statistics');
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [queryString],
  );

  useEffect(() => {
    // A non-admin never gets as far as rendering the data, so there is nothing
    // to fetch and no loading flag to settle — the denial panel is returned
    // above regardless of it.
    if (!canRead) return;
    const controller = new AbortController();
    // The date inputs fire per keystroke; the same 250ms guard the list uses
    // keeps a half-typed year from costing a round trip.
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, canRead]);

  const rows = useMemo(() => result?.data ?? [], [result]);
  const meta = result?.meta ?? null;
  const initialLoad = loading && !result;

  // Ordered by volume, so the first row is the scale — but derive it from the
  // data rather than assuming, and never divide by zero.
  const peak = useMemo(() => rows.reduce((max, row) => Math.max(max, row.hits), 0), [rows]);
  const shownHits = useMemo(() => rows.reduce((sum, row) => sum + row.hits, 0), [rows]);
  const shownErrors = useMemo(() => rows.reduce((sum, row) => sum + row.errors, 0), [rows]);

  /*
   * ⚠️ **`parseLimit` HONOURS ANY INTEGER 1..100, NOT JUST THE FOUR OFFERED**,
   * so `?limit=37` is a live window this page really fetched and no row carries.
   * The stored value is injected back, labelled, exactly as every retired
   * picklist value is in this app — without it the trigger falls back to its
   * placeholder over a real 37-row window and reads as "nothing chosen", and the
   * reader's only way out is to pick one of the four and lose their link.
   *
   * The native `<select>` hid this: with no matching `<option>` it rendered
   * blank, which looks like an empty control rather than a mismatched one.
   */
  const limitOptions = useMemo<ComboboxOption[]>(() => {
    const stored = String(limit);
    if (LIMIT_COMBOBOX_OPTIONS.some((option) => option.value === stored)) {
      return LIMIT_COMBOBOX_OPTIONS;
    }
    return [{ value: stored, label: stored, hint: 'Not a preset' }, ...LIMIT_COMBOBOX_OPTIONS];
  }, [limit]);

  function setParams(changes: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      // An empty query param is a 422 rather than a default, so it is dropped
      // from the URL entirely instead of being carried as a blank.
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function applyPreset(days: number) {
    setParams({ from: daysAgoLocal(days), to: '' });
  }

  function isPreset(days: number): boolean {
    return fromParam === daysAgoLocal(days) && toParam === null;
  }

  if (!canRead) {
    return (
      <Panel aria-labelledby="api-log-stats-heading">
        <PanelHeader id="api-log-stats-heading" title="Activity summary" />
        <EmptyState
          icon={<AlertIcon />}
          title="Not available to your account"
          description="Administrators only. Ask one if you need these figures."
        />
      </Panel>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <Panel aria-labelledby="api-log-stats-heading">
        <PanelHeader
          id="api-log-stats-heading"
          title="Activity summary"
          actions={
            <TextLink to="/audit-log" className="text-xs font-medium">
              Back to the activity log
            </TextLink>
          }
        />

        {/*
         * Filters get their own full-width row rather than PanelHeader's
         * `actions` slot: that slot is `shrink-0`, so a bank of controls there
         * can neither shrink nor wrap and pushes the page out sideways.
         */}
        <div className="flex min-w-0 flex-wrap items-end gap-3 border-b border-border px-4 py-3">
          {/*
           * ⚠️ **THIS DRIVES THE WHOLE QUERY, it is not a display toggle.**
           * Choosing a grouping rewrites `?groupBy=`, which changes
           * `queryString`, which refetches — the buckets, their type, the null
           * label and every noun in the copy around the list all follow from it.
           * `Combobox` commits once per selection exactly as the native select
           * did, so that chain is unchanged; what must not creep in is a blank
           * row. See `GROUPING_OPTIONS`.
           */}
          <div className="w-full sm:w-52">
            <Field label="Break down by">
              {({ id }) => (
                <Combobox
                  id={id}
                  value={groupBy}
                  onChange={(next) => setParams({ groupBy: next })}
                  options={GROUPING_OPTIONS}
                  searchPlaceholder="Search groupings…"
                />
              )}
            </Field>
          </div>
          <div className="w-full sm:w-40">
            <Field label="From">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  max={todayLocal()}
                  value={fromParam ?? ''}
                  onChange={(e) => setParams({ from: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="w-full sm:w-40">
            <Field label="To (inclusive)">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  max={todayLocal()}
                  value={toParam ?? ''}
                  onChange={(e) => setParams({ to: e.target.value })}
                />
              )}
            </Field>
          </div>
          {/* ⚠️ The wrapper is `sm:w-28`, narrower than `Combobox`'s 240px
              popover floor — that is the primitive working as designed (it
              refuses to open a list too narrow to read) and the popover is
              clamped to the viewport, so it cannot overhang. Do not widen this
              track to "match" the list. */}
          <div className="w-full sm:w-28">
            <Field label="Rows">
              {({ id }) => (
                <Combobox
                  id={id}
                  value={String(limit)}
                  onChange={(next) => setParams({ limit: next })}
                  options={limitOptions}
                />
              )}
            </Field>
          </div>
          <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">
            <Button size="sm" aria-pressed={isPreset(1)} onClick={() => applyPreset(1)}>
              24 hours
            </Button>
            <Button size="sm" aria-pressed={isPreset(7)} onClick={() => applyPreset(7)}>
              7 days
            </Button>
            <Button size="sm" aria-pressed={isPreset(30)} onClick={() => applyPreset(30)}>
              30 days
            </Button>
            {(fromParam !== null || toParam !== null) && (
              <Button variant="ghost" size="sm" onClick={() => setParams({ from: '', to: '' })}>
                Reset window
              </Button>
            )}
          </div>
        </div>

        {/*
         * The window, printed from `meta` rather than from local state. On a
         * first visit local state is empty and the server has quietly applied
         * its 7-day default — the one moment these figures are most likely to
         * be read as "all time".
         */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-surface-sunken px-4 py-2">
          <span className="text-xs font-semibold text-fg-subtle">
            Covering
          </span>
          <span className="min-w-0 text-xs text-fg-secondary tabular [overflow-wrap:anywhere]">
            {meta ? `${formatStamp(meta.from)} → ${meta.to === null ? 'now' : formatStamp(meta.to)}` : 'Loading…'}
          </span>
          {meta && fromParam === null && (
            <Badge tone="neutral" variant="outline">
              Default — the last 7 days, not all time
            </Badge>
          )}
          {toParam !== null && (
            <span className="text-2xs text-fg-muted">
              Includes all of {formatDateOnly(toParam)}.
            </span>
          )}
          <span className="text-2xs text-fg-subtle">
            Presets start at midnight.
          </span>
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {initialLoad
            ? 'Loading the activity summary'
            : `${rows.length} ${rows.length === 1 ? grouping.noun : grouping.nounPlural} shown`}
        </p>

        {error && (
          <Notice tone="danger" role="alert" className="m-4" title={<>Couldn&rsquo;t load the summary.</>}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <StatsSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<AlertIcon />}
              title={error ? 'Nothing to show' : 'No activity in this window'}
              description={
                error
                  ? 'Check the message above and try again.'
                  : 'Nothing recorded between those dates. Widen the window.'
              }
              action={
                error ? null : (
                  <Button variant="secondary" size="sm" onClick={() => applyPreset(30)}>
                    Widen to 30 days
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Legend />
              <ul className="m-0 list-none p-0">
                {rows.map((row) => (
                  <StatRow
                    key={bucketKey(row.bucket)}
                    row={row}
                    grouping={grouping}
                    peak={peak}
                    from={meta?.from ?? null}
                    to={meta?.to ?? null}
                  />
                ))}
              </ul>
            </>
          )}
        </div>

        {result && rows.length > 0 && (
          <PanelFooter>
            <p className="m-0 text-xs text-fg-muted tabular">
              {formatCount(shownHits)} requests and {formatCount(shownErrors)} failures across the{' '}
              {rows.length} {rows.length === 1 ? 'row' : 'rows'} shown
              {rows.length === limit ? ` (capped at ${limit})` : ''}.
            </p>
            {/*
             * ⚠️ **"Busiest {nounPlural} only — not totals for the window." IS
             * GONE — owner's ruling, 2026-09-05**, with the rest of this app's
             * helper text. The figures above are still the SHOWN rows only, and
             * the "(capped at N)" clause is the one remaining signal that the
             * list is truncated. **The figures are not totals for the window and
             * nothing now says so.**
             */}
          </PanelFooter>
        )}
      </Panel>
    </div>
  );
}

/**
 * What the two bands of every bar mean.
 *
 * Colour is never the only signal here — each row prints its requests, its
 * failures and its failure rate as figures — but the legend still has to name
 * the bands for anyone reading the bars first.
 */
function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-4 py-2 text-2xs text-fg-muted">
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="h-2 w-3 rounded-xs bg-chart-2" />
        Went through
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="h-2 w-3 rounded-xs bg-danger" />
        Failed
      </span>
      <span>Bars scale to the busiest row.</span>
    </div>
  );
}

/**
 * One aggregate row.
 *
 * The bucket carries three different types depending on `groupBy`, and that
 * branch is the point of `BucketLabel`: a status code is a number and earns a
 * status-toned badge, a user id is a BIGINT string that must not be parsed, and
 * `null` is a real bucket with a name of its own.
 *
 * The reading order is deliberate — what it is, how much of it there was, how
 * much of it failed, and only then how long it took. Response times are the
 * most technical thing on the page and the reason support keeps it, so they
 * stay; they just do not lead.
 */
function StatRow({
  row,
  grouping,
  peak,
  from,
  to,
}: {
  row: ApiLogStatRow;
  grouping: Grouping;
  peak: number;
  from: string | null;
  to: string | null;
}) {
  const rate = errorRate(row);
  const share = peak > 0 ? row.hits / peak : 0;
  const okWidth = peak > 0 ? ((row.hits - row.errors) / peak) * 100 : 0;
  const errorWidth = peak > 0 ? (row.errors / peak) * 100 : 0;

  /*
   * A row links into the feed only when the feed can actually filter on it. A
   * `null` bucket has no filter — there is no "route IS NULL" param — so it
   * stays plain text rather than offering a link that would quietly return the
   * unfiltered log.
   */
  const href = useMemo(() => {
    if (row.bucket === null) return null;
    const query = new URLSearchParams();
    /*
     * `route` is an EXACT match server-side, so the pattern goes verbatim —
     * `/api/v1/invoices/:id`, colon and all. `userId` is the BIGINT string and
     * is likewise copied rather than parsed. Neither is ever the humanised
     * label: the label is for the reader, the slug is for the wire.
     */
    query.set(grouping.listParam, typeof row.bucket === 'number' ? String(row.bucket) : row.bucket);
    // Carry the window the figures were computed over, so the feed shows the
    // same slice of time rather than its own default.
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    return `/audit-log?${query.toString()}`;
  }, [row.bucket, grouping.listParam, from, to]);

  return (
    <li className="grid min-w-0 gap-1.5 border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <BucketLabel row={row} grouping={grouping} href={href} />
        </div>
        <span className="shrink-0 text-sm font-medium text-fg tabular">
          {formatCount(row.hits)}
          <span className="ml-1 text-2xs font-normal text-fg-subtle">
            {row.hits === 1 ? 'request' : 'requests'}
          </span>
        </span>
      </div>

      {/*
       * Decorative: the same figures are printed below as text, so the bar adds
       * nothing for a screen reader but noise.
       */}
      <div
        aria-hidden="true"
        className="flex h-2 w-full overflow-hidden rounded-xs bg-surface-sunken"
      >
        <span className="block bg-chart-2" style={{ width: `${okWidth}%` }} />
        <span
          className="block bg-danger"
          style={{ width: `${errorWidth === 0 ? 0 : Math.max(errorWidth, 0.75)}%` }}
        />
      </div>

      {/* The figures a manager reads. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-fg-muted tabular">
        <span>{(share * 100).toFixed(1)}% of the busiest row</span>
        <span>
          {formatCount(row.errors)} {row.errors === 1 ? 'failure' : 'failures'}
        </span>
        <ErrorRateBadge errors={row.errors} rate={rate} />
      </div>

      {/*
       * Response times, demoted. Kept because support tunes endpoints from
       * them; set below the counts and in the quietest text on the row because
       * nobody else needs them. `null` is "not recorded" and renders as a dash
       * — never as 0, and never as NaN.
       */}
      <p className="m-0 min-w-0 text-2xs text-fg-subtle tabular">
        Response time: {formatNullableLatency(row.avgDurationMs)} average,{' '}
        {formatNullableLatency(row.maxDurationMs)} slowest
      </p>
    </li>
  );
}

/**
 * The bucket, rendered by its actual type and in the reader's words.
 *
 * `label` is populated for `userId` alone, and is null there too once the
 * account has been deleted — so the fallback is the id itself, never a blank.
 */
function BucketLabel({
  row,
  grouping,
  href,
}: {
  row: ApiLogStatRow;
  grouping: Grouping;
  href: string | null;
}) {
  const linkClass = 'text-sm font-medium [overflow-wrap:anywhere]';

  // A real bucket with no value of its own. It is named, and what it means
  // depends on the grouping. Deliberately not linked: there is no "IS NULL"
  // filter, so a link here would land on the unfiltered log.
  //
  // ⚠️ **`nullLabel` ALONE — owner's ruling, 2026-09-05.** The `nullHint` that
  // sat beside it ("Sign-ins, health checks and permission look-ups.", and its
  // five siblings) explained what the null bucket means; that is an explanation
  // rather than an error or a refusal, so it went with the rest of this app's
  // helper text. **The label still NAMES the bucket** — it is never a bare dash,
  // which is the part that mattered.
  if (row.bucket === null) {
    return (
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-medium text-fg-secondary">{grouping.nullLabel}</span>
      </span>
    );
  }

  // A number: only `statusCode` groups this way, and it reads as a status.
  if (typeof row.bucket === 'number') {
    const code = row.bucket;
    return (
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <Badge tone={statusTone(code)} dot>
          Result {code}
        </Badge>
        {href && (
          <TextLink to={href} tone="muted" className="text-2xs underline">
            See this activity
          </TextLink>
        )}
      </span>
    );
  }

  /*
   * A string from here down. For `userId` that string is a BIGINT the pool
   * hands back as text so nothing is lost to a float — it is displayed and
   * linked as-is, never put through Number().
   */
  if (grouping.value === 'userId') {
    const name = row.label;
    const fallback = `Person #${row.bucket}`;
    return (
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {href ? (
          <TextLink to={href} className={linkClass}>
            {name ?? fallback}
          </TextLink>
        ) : (
          <span className="text-sm font-medium text-fg">{name ?? fallback}</span>
        )}
        {name === null ? (
          <span className="text-2xs text-fg-subtle">
            No name on the account.
          </span>
        ) : (
          <span className="text-2xs text-fg-subtle tabular">#{row.bucket}</span>
        )}
      </span>
    );
  }

  /*
   * Everything else. The slug groupings get their business name; the raw value
   * stays as the title so a support reader can still see exactly what was
   * aggregated.
   */
  const raw = row.bucket;
  const text = bucketText(grouping, raw);
  return href ? (
    <TextLink to={href} className={linkClass} title={raw}>
      {text}
    </TextLink>
  ) : (
    <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]" title={raw}>
      {text}
    </span>
  );
}

/** Failures as a share, toned — with the figure always beside the tone. */
function ErrorRateBadge({ errors, rate }: { errors: number; rate: number | null }) {
  if (errors === 0) return <Badge tone="success">0% failed</Badge>;
  const tone = rate !== null && rate >= 0.25 ? 'danger' : 'warning';
  return <Badge tone={tone}>{formatRate(rate)} failed</Badge>;
}

/** The loading state in the shape of the real rows, not a bare spinner. */
function StatsSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="grid gap-2 border-b border-border px-4 py-3 last:border-b-0">
          <div className="flex items-baseline justify-between gap-3">
            <Skeleton className="h-3 w-40 max-w-[60%]" />
            <Skeleton className="h-3 w-12" />
          </div>
          <Skeleton className="h-2 w-full rounded-xs" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-2.5 w-20" />
          </div>
          <Skeleton className="h-2.5 w-36" />
        </li>
      ))}
    </ul>
  );
}
